import React from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { SectionLabel } from '../components/SectionLabel';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
import { DeviceMockup } from '../components/visuals/DeviceMockup';
import { CalendarVisual } from '../components/visuals/CalendarVisual';
import { ReengageVisual } from '../components/visuals/ReengageVisual';

/* 2026-09-15 (Kevin, three-services restructure): the first page of the
   "Reception" dropdown (renamed from "Business Tools" the same day). Kevin's
   brief for this page: the roofer does not care about AI, he cares about
   leads he cannot answer, calls he misses, invoices nobody paid, no-shows,
   and a contact list he never re-engaged -- six outcome sections in that
   order, each naming the piece of the system that does it in one line.
   Visuals reuse the language of the existing DeviceMockup mockups (v110)
   rather than repeating them verbatim; two new visuals (CalendarVisual,
   ReengageVisual) cover the scheduling and re-engagement outcomes those
   mockups did not reach. Every animation respects prefers-reduced-motion
   via the components it is built from. */

const outcomes = [{
  label: 'Outcome 01',
  title: 'Every lead answered while you are on a job',
  piece: 'Auto-reply',
  body: 'A quote request comes in through Yelp while you are up on a ladder. It gets a reply in seconds, not whenever you next check your phone.',
  visual: <DeviceMockup mode="chat" heading="Yelp quote request" lines={[{
    from: 'customer',
    text: 'Hi, looking for a quote on a roof repair, small leak near the chimney.'
  }, {
    from: 'ai',
    text: 'Got it. What is the address and best time this week for someone to take a look?'
  }]} resultChip="Replied in 40 seconds" data-id="element-3597" data-mp="2hnzlzpbzebbj" />
}, {
  label: 'Outcome 02',
  title: 'Calls answered while you sleep',
  piece: 'AI receptionist',
  body: 'A pipe bursts at 2:14 AM. Somebody picks up, asks the right questions, and gets it on your calendar before you wake up.',
  visual: <DeviceMockup mode="call" heading="Incoming call, 2:14 AM" lines={[{
    from: 'caller',
    text: "There's water coming through my kitchen ceiling, I need someone out here."
  }, {
    from: 'ai',
    text: 'I can get someone there first thing this morning. Can I get your address and confirm 7:30 AM works?'
  }]} resultChip="Booked for 7:30 AM" data-id="element-3598" data-mp="3qmn6asng07nt" />
}, {
  label: 'Outcome 03',
  title: 'Invoice and estimate reminders that get you paid',
  piece: 'CRM reminders',
  body: 'An invoice that sits unpaid gets a nudge on its own. You stop being the person who has to remember to ask twice.',
  visual: <DeviceMockup mode="chat" heading="Invoice #1842" lines={[{
    from: 'system',
    text: 'Reminder: invoice #1842 for $640 is due Friday.'
  }, {
    from: 'customer',
    text: 'Sorry, missed this, paying now.'
  }]} resultChip="Paid" data-id="element-3599" data-mp="15yipwgc6coy9" />
}, {
  label: 'Outcome 04',
  title: 'Appointment reminders and rescheduling',
  piece: 'CRM reminders',
  body: 'Customers get reminded before the appointment, and a conflict moves itself instead of turning into an empty driveway.',
  visual: <CalendarVisual mode="reschedule" heading="This week" data-id="element-3600" data-mp="32qikkdqb4ybh" />
}, {
  label: 'Outcome 05',
  title: 'Customers book into your calendar themselves',
  piece: 'Booking link',
  body: 'Send one link. They pick a time that works for both of you, and it lands on your calendar without a round of texts.',
  visual: <CalendarVisual mode="selfBook" heading="Booking link" data-id="element-3601" data-mp="3gigmo4llpty5" />
}, {
  label: 'Outcome 06',
  title: 'Your old contact list re-engaged',
  piece: 'Campaigns',
  body: 'The customers from last year who never came back get a reason to. A short campaign wakes the list up instead of letting it sit.',
  visual: <ReengageVisual data-id="element-3602" data-mp="3cscgh02zwr9u" />
}];
export function WhatItFixes() {
  return <>
      <SEOHead title="What It Fixes | The Viridia System | Viridia Analytics" description="Every lead answered, calls answered while you sleep, invoices that get paid, appointments that keep themselves, and an old contact list waking back up. What The Viridia System actually fixes." canonical="/what-it-fixes" data-id="element-3603" data-mp="37ut11wuka8gb" />

      <PageHeader label="Reception" title="What it fixes." intro="Not features. The six things that actually cost you money when nobody is watching them." data-id="element-3604" data-mp="2owq6z3n5plbn">
        <CTAButton to="/pricing#viridia-system" data-id="element-3605" data-mp="2yunv7r50ni49">See pricing for The Viridia System</CTAButton>
      </PageHeader>

      {outcomes.map((o, i) => <section key={o.title} className={`relative isolate overflow-hidden border-b border-[#1a1a1a] ${i % 2 === 1 ? 'bg-[#0c0c0c]' : ''}`} data-id="element-3606" data-mp="j2gpb4ibybjk" data-mpi={i}>
          <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:py-24" data-id="element-3607" data-mp="lmwl75bjuj8g">
            <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4
        }} className={i % 2 === 1 ? 'lg:order-2' : ''} data-id="element-3608" data-mp="qoc8z55433uj">
              <SectionLabel data-id="element-3609" data-mp="14ez5cqneghng">{o.label}</SectionLabel>
              <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3610" data-mp="19e227nt87hkc">
                {o.title}
              </h2>
              <p className="mt-5 max-w-md text-lg leading-relaxed text-neutral-400" data-id="element-3611" data-mp="vlvo079mvknj">{o.body}</p>
              <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-emerald-500" data-id="element-3612" data-mp="zlwckkqg5c8b">
                {o.piece}
              </p>
            </motion.div>

            <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4,
          delay: 0.08
        }} className={i % 2 === 1 ? 'lg:order-1' : ''} data-id="element-3613" data-mp="1s4a4k3p3q3zh">
              {o.visual}
            </motion.div>
          </div>
        </section>)}

      <section className="relative overflow-hidden" data-id="element-3614" data-mp="1o10qj9wsdvuu">
        <div className="mx-auto w-full max-w-3xl px-6 py-20 text-center lg:py-24" data-id="element-3615" data-mp="1o2llv1qxditr">
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3616" data-mp="1j2frhdt9ti7z">
            All six run inside one system.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-3617" data-mp="1a81exj04klsk">
            The Viridia System is $897 a month, month to month.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4" data-id="element-3618" data-mp="10an8mc7x1ddz">
            <CTAButton to="/pricing#viridia-system" data-id="element-3619" data-mp="10cr8mj2p1s8z">See what is included</CTAButton>
            <CTAButton to="/getting-started" variant="ghost" data-id="element-3620" data-mp="vcbz62lj94wa">
              How it starts
            </CTAButton>
          </div>
        </div>
      </section>
    </>;
}